// CR075-P5: Clear Staging Dialog - Full clear or derived-only clear
import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Eraser, AlertTriangle, Loader2, ShieldAlert, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ClearStagingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
  onCleared: () => void;
}

interface ClearCounts {
  staging_items: number;
  normalized_deals: number;
  deduped_deals: number;
}

export function ClearStagingDialog({
  open,
  onOpenChange,
  batchRunId,
  onCleared,
}: ClearStagingDialogProps) {
  const { toast } = useToast();
  const [clearType, setClearType] = useState<"FULL" | "DERIVED_ONLY">("DERIVED_ONLY");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [isClearing, setIsClearing] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [counts, setCounts] = useState<ClearCounts | null>(null);
  const [loadingCounts, setLoadingCounts] = useState(false);

  const expectedConfirm = clearType === "FULL" ? "CLEAR" : "CLEAR DERIVED";
  const canConfirm = confirmText.toUpperCase() === expectedConfirm;

  // Check if feature is enabled
  useEffect(() => {
    const checkFlag = async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("enabled")
        .eq("flag_name", "ff_batch_runs_clear_staging_enabled")
        .single();
      setFeatureEnabled(data?.enabled ?? false);
    };
    if (open) {
      checkFlag();
    }
  }, [open]);

  // Fetch counts when dialog opens
  useEffect(() => {
    const fetchCounts = async () => {
      if (!open) return;
      setLoadingCounts(true);
      try {
        const [stagingResult, normalizedResult, dedupedResult] = await Promise.all([
          supabase
            .from("cr075_staging_deal_items")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
          supabase
            .from("cr075_staging_deals_normalized")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
          supabase
            .from("cr075_staging_deals_deduped")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
        ]);

        setCounts({
          staging_items: stagingResult.count || 0,
          normalized_deals: normalizedResult.count || 0,
          deduped_deals: dedupedResult.count || 0,
        });
      } catch (err) {
        console.error("Failed to fetch counts:", err);
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, [open, batchRunId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReason("");
      setClearType("DERIVED_ONLY");
      setCounts(null);
    }
  }, [open]);

  const handleClear = async () => {
    if (!canConfirm || isClearing) return;

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke("clear-batch-staging", {
        body: {
          batch_run_id: batchRunId,
          clear_type: clearType,
          reason: reason || undefined,
        },
      });

      if (error) throw error;

      toast({
        title: clearType === "FULL" ? "Staging cleared" : "Derived data cleared",
        description:
          clearType === "FULL"
            ? `Cleared ${data.counts?.staging_items || 0} staging items + derived data. Steps reset.`
            : `Cleared ${data.counts?.normalized_deals || 0} normalized + ${data.counts?.deduped_deals || 0} deduped. Steps S4-S8 reset.`,
      });

      onOpenChange(false);
      onCleared();
    } catch (err) {
      console.error("Failed to clear staging:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to clear staging",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Eraser className="h-5 w-5 text-orange-500" />
            Clear Staging Data
          </AlertDialogTitle>
          <AlertDialogDescription>
            Reset staging data for re-testing. Run ID: <code className="text-xs">{batchRunId.slice(0, 8)}</code>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!featureEnabled ? (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Feature Disabled</AlertTitle>
            <AlertDescription>
              Clear staging is disabled. Enable <code>ff_batch_runs_clear_staging_enabled</code> feature flag to use this feature.
            </AlertDescription>
          </Alert>
        ) : (
          <Tabs value={clearType} onValueChange={(v) => setClearType(v as "FULL" | "DERIVED_ONLY")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="DERIVED_ONLY" className="gap-2">
                <Layers className="h-4 w-4" />
                Derived Only
              </TabsTrigger>
              <TabsTrigger value="FULL" className="gap-2">
                <Eraser className="h-4 w-4" />
                Full Clear
              </TabsTrigger>
            </TabsList>

            <TabsContent value="DERIVED_ONLY" className="space-y-4 mt-4">
              <Alert>
                <Layers className="h-4 w-4" />
                <AlertTitle>Clear Derived Data</AlertTitle>
                <AlertDescription>
                  Clears Normalized (S4) + Deduped (S5) tables. Keeps staging items from collection (S1-S3).
                  Resets steps S4-S8 to PENDING.
                </AlertDescription>
              </Alert>

              <div className="rounded-lg border p-4 space-y-2">
                <div className="text-sm font-medium">Records to be cleared:</div>
                {loadingCounts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : counts ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Normalized: <strong>{counts.normalized_deals}</strong></div>
                    <div>Deduped: <strong>{counts.deduped_deals}</strong></div>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            <TabsContent value="FULL" className="space-y-4 mt-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Full Clear (All Staging)</AlertTitle>
                <AlertDescription>
                  Clears ALL staging data including collection results (S1-S3), normalized, and deduped.
                  All steps reset to PENDING. You'll need to re-run collection.
                </AlertDescription>
              </Alert>

              <div className="rounded-lg border p-4 space-y-2">
                <div className="text-sm font-medium">Records to be cleared:</div>
                {loadingCounts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : counts ? (
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>Staging: <strong>{counts.staging_items}</strong></div>
                    <div>Normalized: <strong>{counts.normalized_deals}</strong></div>
                    <div>Deduped: <strong>{counts.deduped_deals}</strong></div>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Why are you clearing this data?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">
                  Type <code className="px-1 py-0.5 bg-muted rounded">{expectedConfirm}</code> to confirm
                </Label>
                <Input
                  id="confirm"
                  placeholder={expectedConfirm}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  autoComplete="off"
                />
              </div>
            </div>
          </Tabs>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isClearing}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleClear}
            disabled={!canConfirm || isClearing || !featureEnabled}
          >
            {isClearing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Clearing...
              </>
            ) : clearType === "FULL" ? (
              "Clear All Staging"
            ) : (
              "Clear Derived Data"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
